import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Swal from "sweetalert2";
import {
  addPhysiotherapySession,
  updatePhysiotherapySession,
} from "../../../services/physiotherapyApi";

export default function SessionModal({
  open,
  onClose,
  onSuccess,
  physioCase,
  editingSession = null,
}) {
  const [formData, setFormData] = useState({
    sessionDate: new Date().toISOString().split("T")[0],
    conductedBy: "",
    durationMinutes: 45,
    status: "Completed",
    painLevelBefore: 5,
    painLevelAfter: 3,
    patientResponse: "Good Tolerance",
    mobilityAssessment: "Improved gait",
    progressRemarks: "",
    nextSessionDate: "",
  });

  const [exercisesList, setExercisesList] = useState([]);
  const [newExercise, setNewExercise] = useState({
    exerciseName: "",
    sets: 3,
    repetitions: 10,
    holdTimeSeconds: 5,
  });

  const [attachments, setAttachments] = useState([]);
  const [saving, setSaving] = useState(false);
  const contentRef = React.useRef(null);

  useEffect(() => {
    if (open) {
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
      if (editingSession) {
        const conductedStr =
          typeof editingSession.conductedBy === "object"
            ? editingSession.conductedBy?.name || ""
            : editingSession.conductedBy || "";

        setFormData({
          sessionDate: editingSession.sessionDate
            ? String(editingSession.sessionDate).slice(0, 10)
            : new Date().toISOString().split("T")[0],
          conductedBy: conductedStr || physioCase?.assignedPhysiotherapist?.name || "",
          durationMinutes: editingSession.durationMinutes || 45,
          status: editingSession.status || "Completed",
          painLevelBefore: editingSession.painLevelBefore ?? 5,
          painLevelAfter: editingSession.painLevelAfter ?? 3,
          patientResponse: editingSession.patientResponse || "Good Tolerance",
          mobilityAssessment: editingSession.mobilityAssessment || editingSession.mobilityStatus || "",
          progressRemarks: editingSession.progressRemarks || "",
          nextSessionDate: editingSession.nextSessionDate
            ? String(editingSession.nextSessionDate).slice(0, 10)
            : "",
        });

        const rawEx = editingSession.exercises || editingSession.exercisesPerformed || [];
        const normEx = rawEx.map((e) =>
          typeof e === "string"
            ? { exerciseName: e, sets: 3, repetitions: 10, holdTimeSeconds: 5 }
            : {
                exerciseName: e.exerciseName || e.name || "",
                sets: Number(e.sets) || 3,
                repetitions: Number(e.repetitions || e.reps) || 10,
                holdTimeSeconds: Number(e.holdTimeSeconds) || 5,
              }
        );
        setExercisesList(normEx);
        setAttachments([]);
      } else {
        setFormData({
          sessionDate: new Date().toISOString().split("T")[0],
          conductedBy: physioCase?.assignedPhysiotherapist?.name || "",
          durationMinutes: 45,
          status: "Completed",
          painLevelBefore: physioCase?.initialAssessment?.baselinePainScore ?? 5,
          painLevelAfter: Math.max(0, (physioCase?.initialAssessment?.baselinePainScore ?? 5) - 2),
          patientResponse: "Good Tolerance",
          mobilityAssessment: "",
          progressRemarks: "",
          nextSessionDate: "",
        });
        setExercisesList([]);
        setAttachments([]);
      }
    }
  }, [open, physioCase, editingSession]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddExercise = () => {
    if (!newExercise.exerciseName.trim()) return;
    setExercisesList((prev) => [...prev, { ...newExercise }]);
    setNewExercise({
      exerciseName: "",
      sets: 3,
      repetitions: 10,
      holdTimeSeconds: 5,
    });
  };

  const handleRemoveExercise = (idx) => {
    setExercisesList((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleFileChange = (e) => {
    if (e.target.files) {
      setAttachments(Array.from(e.target.files));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!physioCase?._id) {
      return Swal.fire("Error", "Missing Physiotherapy Case reference", "error");
    }

    setSaving(true);
    try {
      const sessNumber = editingSession?.sessionNumber || currentSessionNumber;
      const sessId = editingSession?._id || editingSession?.sessionId || editingSession?.id;

      if (attachments.length > 0) {
        const data = new FormData();
        data.append("sessionDate", formData.sessionDate);
        data.append("conductedBy", formData.conductedBy.trim());
        data.append("durationMinutes", Number(formData.durationMinutes) || 45);
        data.append("status", formData.status);
        data.append("painLevelBefore", Number(formData.painLevelBefore) || 0);
        data.append("painLevelAfter", Number(formData.painLevelAfter) || 0);
        data.append("patientResponse", formData.patientResponse);
        data.append("mobilityAssessment", formData.mobilityAssessment);
        data.append("progressRemarks", formData.progressRemarks);
        if (formData.nextSessionDate) {
          data.append("nextSessionDate", formData.nextSessionDate);
        }

        data.append("exercises", JSON.stringify(exercisesList));

        attachments.forEach((file) => {
          data.append("attachments", file);
        });

        if (editingSession) {
          if (sessId) data.append("sessionId", sessId);
          data.append("sessionNumber", sessNumber);
          await updatePhysiotherapySession(physioCase._id, sessId || sessNumber, data);
          Swal.fire({
            title: "Session Updated!",
            text: `Physiotherapy session #${sessNumber} has been updated successfully.`,
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
        } else {
          await addPhysiotherapySession(physioCase._id, data);
          Swal.fire({
            title: "Session Recorded!",
            text: "The physiotherapy session and patient evaluations have been logged.",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
        }
      } else {
        const payload = {
          ...(sessId ? { sessionId: sessId } : {}),
          sessionNumber: sessNumber,
          sessionDate: formData.sessionDate,
          conductedBy: formData.conductedBy.trim(),
          durationMinutes: Number(formData.durationMinutes) || 45,
          status: formData.status,
          painLevelBefore: Number(formData.painLevelBefore) || 0,
          painLevelAfter: Number(formData.painLevelAfter) || 0,
          patientResponse: formData.patientResponse,
          mobilityAssessment: formData.mobilityAssessment,
          progressRemarks: formData.progressRemarks,
          ...(formData.nextSessionDate ? { nextSessionDate: formData.nextSessionDate } : {}),
          exercises: exercisesList,
        };

        if (editingSession) {
          await updatePhysiotherapySession(physioCase._id, sessId || sessNumber, payload);
          Swal.fire({
            title: "Session Updated!",
            text: `Physiotherapy session #${sessNumber} has been updated successfully.`,
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
        } else {
          await addPhysiotherapySession(physioCase._id, payload);
          Swal.fire({
            title: "Session Recorded!",
            text: "The physiotherapy session and patient evaluations have been logged.",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
        }
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      const msg =
        typeof error === "string"
          ? error
          : error?.message ||
            (editingSession ? "Failed to update session" : "Failed to record session");
      Swal.fire("Error", msg, "error");
    } finally {
      setSaving(false);
    }
  };

  const currentSessionNumber = (physioCase?.completedSessions || 0) + 1;
  const totalSessions = physioCase?.prescribedSessions || 10;
  const painDelta = formData.painLevelBefore - formData.painLevelAfter;

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
      className="physio-session-dialog"
      scroll="paper"
      disableAutoFocus
      disableRestoreFocus
      PaperProps={{
        style: {
          borderRadius: "16px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          maxHeight: "92vh",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
        },
      }}
    >
      {/* Sticky Emerald Header */}
      <div
        style={{
          background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
          color: "#ffffff",
          padding: "16px 22px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <div className="d-flex align-items-center gap-3">
          <div
            style={{
              width: "42px",
              height: "42px",
              borderRadius: "10px",
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(4px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#ffffff",
              fontSize: "20px",
            }}
          >
            <i className="fa-solid fa-calendar-check" />
          </div>
          <div>
            <h5 className="mb-0 text-white fw-bold" style={{ fontSize: "17px", letterSpacing: "-0.01em" }}>
              {editingSession
                ? `Edit Physiotherapy Session #${editingSession.sessionNumber || ""}`
                : `Log Physiotherapy Session #${currentSessionNumber}`}
            </h5>
            {physioCase && (
              <small style={{ color: "rgba(255,255,255,0.85)", fontSize: "12.5px" }}>
                Patient: <strong>{physioCase.patientName || physioCase.patientObjectId?.patient_name}</strong> ({physioCase.patientId}) | Plan: {physioCase.completedSessions || 0} of {totalSessions} completed
              </small>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          style={{
            background: "rgba(255,255,255,0.15)",
            border: "none",
            borderRadius: "50%",
            width: "34px",
            height: "34px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#ffffff",
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
          title="Close"
        >
          <i className="fa-solid fa-xmark fs-6" />
        </button>
      </div>

      {/* Scrollable Form Body */}
      <DialogContent
        ref={contentRef}
        style={{
          overflowY: "auto",
          padding: "20px 24px",
          backgroundColor: "#f8fafc",
        }}
      >
        <form id="session-form" onSubmit={handleSubmit}>
          {/* Section 1: Session Details */}
          <div
            className="card mb-3 border-0 shadow-sm"
            style={{ borderRadius: "12px", background: "#ffffff" }}
          >
            <div className="card-body p-3">
              <div className="d-flex align-items-center gap-2 mb-3">
                <span
                  style={{
                    backgroundColor: "#d1fae5",
                    color: "#059669",
                    fontWeight: "700",
                    fontSize: "12px",
                    padding: "3px 8px",
                    borderRadius: "6px",
                  }}
                >
                  INFO
                </span>
                <span className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                  Session Schedule & Therapist
                </span>
              </div>

              <div className="row g-3">
                <div className="col-md-3">
                  <label className="fw-semibold text-dark mb-1" style={{ fontSize: "13px" }}>
                    Session Date
                  </label>
                  <input
                    type="date"
                    name="sessionDate"
                    className="form-control"
                    value={formData.sessionDate}
                    onChange={handleChange}
                    required
                    style={{ fontSize: "13.5px" }}
                  />
                </div>

                <div className="col-md-4">
                  <label className="fw-semibold text-dark mb-1" style={{ fontSize: "13px" }}>
                    Conducted By (Therapist)
                  </label>
                  <input
                    type="text"
                    name="conductedBy"
                    className="form-control"
                    placeholder="Physiotherapist Name"
                    value={formData.conductedBy}
                    onChange={handleChange}
                    style={{ fontSize: "13.5px" }}
                  />
                </div>

                <div className="col-md-2">
                  <label className="fw-semibold text-dark mb-1" style={{ fontSize: "13px" }}>
                    Duration (mins)
                  </label>
                  <input
                    type="number"
                    name="durationMinutes"
                    className="form-control"
                    min="5"
                    max="180"
                    value={formData.durationMinutes}
                    onChange={handleChange}
                    required
                    style={{ fontSize: "13.5px" }}
                  />
                </div>

                <div className="col-md-3">
                  <label className="fw-semibold text-dark mb-1" style={{ fontSize: "13px" }}>
                    Session Status
                  </label>
                  <select
                    name="status"
                    className="form-select"
                    value={formData.status}
                    onChange={handleChange}
                    style={{ fontSize: "13.5px" }}
                  >
                    <option value="Completed">Completed</option>
                    <option value="Scheduled">Scheduled</option>
                    <option value="Missed">Missed</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Pain Evaluation */}
          <div
            className="card mb-3 border-0 shadow-sm"
            style={{ borderRadius: "12px", background: "#ffffff" }}
          >
            <div className="card-body p-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center gap-2">
                  <span
                    style={{
                      backgroundColor: "#fee2e2",
                      color: "#dc2626",
                      fontWeight: "700",
                      fontSize: "12px",
                      padding: "3px 8px",
                      borderRadius: "6px",
                    }}
                  >
                    PAIN SCORE
                  </span>
                  <span className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                    VAS Pain Scale (0 - 10) Before & After
                  </span>
                </div>
                {painDelta > 0 && (
                  <span className="badge bg-success px-3 py-1 fw-bold">
                    <i className="fa fa-arrow-down me-1" />
                    Relief: -{painDelta} Points
                  </span>
                )}
              </div>

              <div className="row g-3">
                <div className="col-md-6">
                  <div className="p-3 border rounded bg-light">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="fw-bold text-dark" style={{ fontSize: "13px" }}>
                        Pain BEFORE Session:
                      </span>
                      <strong
                        className="badge"
                        style={{
                          backgroundColor:
                            formData.painLevelBefore > 6
                              ? "#ef4444"
                              : formData.painLevelBefore > 3
                              ? "#f59e0b"
                              : "#10b981",
                          color: "#fff",
                          fontSize: "12px",
                        }}
                      >
                        {formData.painLevelBefore} / 10
                      </strong>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="10"
                      name="painLevelBefore"
                      className="form-range mt-2"
                      value={formData.painLevelBefore}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="p-3 border rounded bg-light">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="fw-bold text-dark" style={{ fontSize: "13px" }}>
                        Pain AFTER Session:
                      </span>
                      <strong
                        className="badge"
                        style={{
                          backgroundColor:
                            formData.painLevelAfter > 6
                              ? "#ef4444"
                              : formData.painLevelAfter > 3
                              ? "#f59e0b"
                              : "#10b981",
                          color: "#fff",
                          fontSize: "12px",
                        }}
                      >
                        {formData.painLevelAfter} / 10
                      </strong>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="10"
                      name="painLevelAfter"
                      className="form-range mt-2"
                      value={formData.painLevelAfter}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Exercises */}
          <div
            className="card mb-3 border-0 shadow-sm"
            style={{ borderRadius: "12px", background: "#ffffff" }}
          >
            <div className="card-body p-3">
              <div className="d-flex align-items-center gap-2 mb-2">
                <span
                  style={{
                    backgroundColor: "#fef3c7",
                    color: "#b45309",
                    fontWeight: "700",
                    fontSize: "12px",
                    padding: "3px 8px",
                    borderRadius: "6px",
                  }}
                >
                  EXERCISES
                </span>
                <span className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                  Prescribed & Performed Exercises
                </span>
              </div>

              {exercisesList.length > 0 && (
                <div className="table-responsive mb-2 border rounded">
                  <table className="table table-sm table-striped mb-0" style={{ fontSize: "13px" }}>
                    <thead className="table-light">
                      <tr>
                        <th>Exercise Name</th>
                        <th style={{ width: "80px" }}>Sets</th>
                        <th style={{ width: "80px" }}>Reps</th>
                        <th style={{ width: "90px" }}>Hold (s)</th>
                        <th style={{ width: "60px" }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {exercisesList.map((ex, idx) => (
                        <tr key={idx}>
                          <td className="fw-semibold text-dark">{ex.exerciseName}</td>
                          <td>{ex.sets}</td>
                          <td>{ex.repetitions}</td>
                          <td>{ex.holdTimeSeconds ? `${ex.holdTimeSeconds}s` : "-"}</td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm text-danger p-0"
                              onClick={() => handleRemoveExercise(idx)}
                              title="Remove"
                            >
                              <i className="fa fa-trash-can" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="row g-2 align-items-end mt-1">
                <div className="col-md-5">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Add exercise (e.g. Wall Squats)..."
                    value={newExercise.exerciseName}
                    onChange={(e) =>
                      setNewExercise({ ...newExercise, exerciseName: e.target.value })
                    }
                  />
                </div>
                <div className="col-md-2">
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    placeholder="Sets"
                    value={newExercise.sets}
                    onChange={(e) =>
                      setNewExercise({ ...newExercise, sets: Number(e.target.value) })
                    }
                  />
                </div>
                <div className="col-md-2">
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    placeholder="Reps"
                    value={newExercise.repetitions}
                    onChange={(e) =>
                      setNewExercise({
                        ...newExercise,
                        repetitions: Number(e.target.value),
                      })
                    }
                  />
                </div>
                <div className="col-md-3">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary w-100 fw-bold"
                    onClick={handleAddExercise}
                  >
                    <i className="fa fa-plus me-1" /> Add Exercise
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Section 5: Progress & Remarks */}
          <div
            className="card border-0 shadow-sm"
            style={{ borderRadius: "12px", background: "#ffffff" }}
          >
            <div className="card-body p-3">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="fw-semibold text-dark mb-1" style={{ fontSize: "13px" }}>
                    Patient Response / Tolerance
                  </label>
                  <input
                    type="text"
                    name="patientResponse"
                    className="form-control"
                    placeholder="e.g. Good tolerance, mild fatigue"
                    value={formData.patientResponse}
                    onChange={handleChange}
                    style={{ fontSize: "13.5px" }}
                  />
                </div>

                <div className="col-md-6">
                  <label className="fw-semibold text-dark mb-1" style={{ fontSize: "13px" }}>
                    Next Scheduled Session Date
                  </label>
                  <input
                    type="date"
                    name="nextSessionDate"
                    className="form-control"
                    value={formData.nextSessionDate}
                    onChange={handleChange}
                    style={{ fontSize: "13.5px" }}
                  />
                </div>

                <div className="col-12">
                  <label className="fw-semibold text-dark mb-1" style={{ fontSize: "13px" }}>
                    Clinical Progress Remarks
                  </label>
                  <textarea
                    name="progressRemarks"
                    rows="2"
                    className="form-control"
                    placeholder="Patient tolerated therapy well; ROM active flexion improved to 95 degrees..."
                    value={formData.progressRemarks}
                    onChange={handleChange}
                    style={{ fontSize: "13.5px" }}
                  />
                </div>

                <div className="col-12">
                  <label className="fw-semibold text-dark mb-1" style={{ fontSize: "13px" }}>
                    Session Attachments (Exercise Chart, Photos)
                  </label>
                  <input
                    type="file"
                    multiple
                    className="form-control"
                    onChange={handleFileChange}
                    style={{ fontSize: "13px" }}
                  />
                </div>
              </div>
            </div>
          </div>
        </form>
      </DialogContent>

      {/* Sticky Action Footer */}
      <div
        style={{
          backgroundColor: "#ffffff",
          padding: "14px 24px",
          borderTop: "1px solid #e2e8f0",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "12px",
          flexShrink: 0,
        }}
      >
        <button
          type="button"
          className="btn btn-light px-4 border fw-semibold"
          style={{ borderRadius: "8px", fontSize: "13.5px" }}
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="submit"
          form="session-form"
          className="btn btn-success px-4 fw-bold d-flex align-items-center gap-2 shadow-sm"
          style={{
            borderRadius: "8px",
            backgroundColor: "#059669",
            borderColor: "#059669",
            fontSize: "13.5px",
          }}
          disabled={saving}
        >
          {saving ? (
            <>
              <i className="fa fa-spinner fa-spin" /> {editingSession ? "Updating Session..." : "Saving Session..."}
            </>
          ) : (
            <>
              <i className="fa fa-check-circle" /> {editingSession ? "Update Session" : "Record Session"}
            </>
          )}
        </button>
      </div>
    </Dialog>
  );
}
